import React, { useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, NumberInput, ColorInput, PanelSection } from "./controls";
import { isArrow, parseLineModel, serializeLineModel, ENDPOINT_TYPES } from "../lineEdit";

const LINECAP_OPTIONS = ["butt", "round", "square"];
const LINEJOIN_OPTIONS = ["miter", "round", "bevel"];

// Human-readable labels for each endpoint decoration type.
const ENDPOINT_LABELS = {
  none: "None",
  round: "Dot",
  square: "Bar",
  "line-arrow": "Line Arrow",
  "triangle-arrow": "Triangle",
  "reversed-triangle": "Reversed Triangle",
  "circle-arrow": "Circle Arrow",
  "diamond-arrow": "Diamond",
};
const FILL_KINDS = [
  { value: "solid", label: "Solid" },
  { value: "linear", label: "Linear Gradient" },
  { value: "radial", label: "Radial Gradient" },
];
const FILTER_KINDS = [
  { value: "none", label: "None" },
  { value: "glow", label: "Glow" },
  { value: "shadow", label: "Drop Shadow" },
  { value: "blur", label: "Blur" },
];

export default function StyleControls({ attrs, setAttr, effects, setEffect }) {
  const opacityValue = useMemo(() => [Math.round((attrs.opacity ?? 1) * 100)], [attrs.opacity]);
  const fill = effects?.fill || { kind: "solid" };
  const filter = effects?.filter || { kind: "none" };
  const arrowEnabled = isArrow({ attrs });

  // Changing an endpoint type requires re-serializing the path `d` so the
  // decoration segments match the new type. We parse the current line model,
  // update the arrowStart/arrowEnd meta-attrs on the base element, then
  // re-serialize — which re-appends both endpoint decorations from the new
  // types. The resulting `d` + meta-attrs are committed in one patch.
  const setEndpoint = (which, type) => {
    if (!arrowEnabled) return;
    const el = { type: "path", attrs };
    const model = parseLineModel(el);
    const base = { ...el, attrs: { ...attrs, [which]: type } };
    const out = serializeLineModel(model, base);
    setAttr({ d: out.attrs.d, [which]: type });
  };

  return (
    <PanelSection title="Style">
      <div className="space-y-2">
        <Field label="Fill">
          <ColorInput value={attrs.fill} onChange={(v) => setAttr("fill", v)} testId="attr-fill-input" />
        </Field>

        <Field label="Fill Type">
          <Select value={fill.kind || "solid"} onValueChange={(v) => setEffect("fill", { kind: v })}>
            <SelectTrigger data-testid="fill-kind-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-chrome border-line text-foreground rounded-none">
              {FILL_KINDS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>

        {fill.kind && fill.kind !== "solid" && (
          <div className="space-y-2 border-l-2 border-[#002FA7] pl-2">
            <Field label="Gradient Color 2">
              <ColorInput value={fill.to || "#7C3AED"} onChange={(v) => setEffect("fill", { to: v })} testId="fill-color2-input" />
            </Field>
            {fill.kind === "linear" && (
              <Field label={`Angle · ${fill.angle ?? 0}°`}>
                <div className="pt-2">
                  <Slider value={[fill.angle ?? 0]} min={0} max={360} step={1} onValueChange={([v]) => setEffect("fill", { angle: v })} data-testid="fill-angle-slider" />
                </div>
              </Field>
            )}
          </div>
        )}

        <Field label="Stroke">
          <ColorInput value={attrs.stroke} onChange={(v) => setAttr("stroke", v)} testId="attr-stroke-input" />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Stroke W">
            <NumberInput
              value={attrs.strokeWidth}
              onChange={(v) => {
                if (arrowEnabled) {
                  // Arrowhead size is derived from strokeWidth (headLen = sw*4,
                  // headWid = sw*3, etc.), so when strokeWidth changes we must
                  // re-serialize the path `d` to regenerate the decorations at
                  // the new size. The arrowhead itself is never manually
                  // resizable — it only changes with the body's stroke width.
                  const el = { type: "path", attrs };
                  const model = parseLineModel(el);
                  const out = serializeLineModel(model, { ...el, attrs: { ...attrs, strokeWidth: v } });
                  setAttr({ strokeWidth: v, d: out.attrs.d });
                } else {
                  setAttr("strokeWidth", v);
                }
              }}
              testId="attr-strokeWidth-input"
            />
          </Field>
          <Field label="Dash">
            <NumberInput value={attrs.strokeDasharray} onChange={(v) => setAttr("strokeDasharray", v)} testId="attr-strokeDasharray-input" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Line Cap">
            <Select value={attrs.strokeLinecap || "butt"} onValueChange={(v) => setAttr("strokeLinecap", v)}>
              <SelectTrigger data-testid="attr-linecap-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                {LINECAP_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Line Join">
            <Select value={attrs.strokeLinejoin || "miter"} onValueChange={(v) => setAttr("strokeLinejoin", v)}>
              <SelectTrigger data-testid="attr-linejoin-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                {LINEJOIN_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {arrowEnabled && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start Point">
              <Select value={attrs.arrowStart || "none"} onValueChange={(v) => setEndpoint("arrowStart", v)}>
                <SelectTrigger data-testid="attr-arrowStart-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {ENDPOINT_TYPES.map((o) => (<SelectItem key={o} value={o}>{ENDPOINT_LABELS[o] || o}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="End Point">
              <Select value={attrs.arrowEnd || "none"} onValueChange={(v) => setEndpoint("arrowEnd", v)}>
                <SelectTrigger data-testid="attr-arrowEnd-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {ENDPOINT_TYPES.map((o) => (<SelectItem key={o} value={o}>{ENDPOINT_LABELS[o] || o}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        <Field label={`Opacity · ${opacityValue[0]}%`}>
          <div className="pt-2">
            <Slider value={opacityValue} min={0} max={100} step={1} onValueChange={([v]) => setAttr("opacity", v / 100)} data-testid="attr-opacity-slider" />
          </div>
        </Field>

        <Field label="Effect / Filter">
          <Select value={filter.kind || "none"} onValueChange={(v) => setEffect("filter", { kind: v })}>
<SelectTrigger data-testid="filter-kind-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-chrome border-line text-foreground rounded-none">
              {FILTER_KINDS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>

        {filter.kind && filter.kind !== "none" && (
          <div className="space-y-2 border-l-2 border-[#FFB400] pl-2">
            <Field label={`Strength · ${filter.strength ?? 4}`}>
              <div className="pt-2">
                <Slider value={[filter.strength ?? 4]} min={0} max={30} step={0.5} onValueChange={([v]) => setEffect("filter", { strength: v })} data-testid="filter-strength-slider" />
              </div>
            </Field>
            {filter.kind !== "blur" && (
              <Field label="Effect Color">
                <ColorInput value={filter.color || (filter.kind === "shadow" ? "#000000" : "#002FA7")} onChange={(v) => setEffect("filter", { color: v })} testId="filter-color-input" />
              </Field>
            )}
            {filter.kind === "shadow" && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Offset X"><NumberInput value={filter.dx ?? 4} onChange={(v) => setEffect("filter", { dx: v })} testId="filter-dx-input" /></Field>
                <Field label="Offset Y"><NumberInput value={filter.dy ?? 4} onChange={(v) => setEffect("filter", { dy: v })} testId="filter-dy-input" /></Field>
              </div>
            )}
          </div>
        )}
      </div>
    </PanelSection>
  );
}
